import { Dispatch, SetStateAction } from 'react'
import has from 'lodash/has'
import { v4 as uuid } from 'uuid'
import { IMessage } from '@stomp/stompjs'

import {
  getSearchRuleForRequest,
  getSearchValueForRequest,
  getSortKeysForQuickSearchForRequest,
} from '@pages/ConfiguredEntity/helpers'

import { useFetchObjectByCodeQuery, useFetchObjectDataRecordEnrichedByIdMutation } from '@redux/api'
import { deleteViewRow, setViewRows } from '@redux/reducers/changedViewRows.reducer'

import { SortType, useAppDispatch, usePkField } from '@hooks'
import { binarySearchFirstOccurrence, convertValueByType, isObjectValueType } from '@helpers'
import {
  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME,
  EVENT_NAME_OBJECT_MESSAGE,
  FIELD_VALUE_TYPE,
} from '@constants'
import { EntityType, ID, MessageBody, ObjectDataRecord, POSTObjectDataFilter } from '@types'

type UseObjectSubscribeWSParams = {
  objectCode: string
  entity: EntityType | null
  objectData: ObjectDataRecord[]
  currentSort: SortType[] | undefined
  setObjectData: Dispatch<SetStateAction<ObjectDataRecord[]>>
  setSelectedRows: Dispatch<SetStateAction<ObjectDataRecord[]>>
  contextId?: ID | undefined
  searchAssistantValue: POSTObjectDataFilter
  hasQuickSearch: boolean
  entityParamsForRequest: Record<string, unknown>
  setTotalElements: Dispatch<SetStateAction<number | null>>
}

export const useObjectSubscribeWS = ({
  objectCode,
  entity,
  objectData,
  currentSort,
  setObjectData,
  contextId,
  searchAssistantValue,
  hasQuickSearch,
  entityParamsForRequest,
  setTotalElements,
  setSelectedRows,
}: UseObjectSubscribeWSParams) => {
  const dispatch = useAppDispatch()

  const { data: objectModel } = useFetchObjectByCodeQuery(objectCode, {
    skip: !objectCode,
  })

  const pkField = usePkField(objectModel?.fields)

  const [fetchEnrichedRow] = useFetchObjectDataRecordEnrichedByIdMutation()

  // Вспомогательная функция для создания строки с уникальным ID
  const createRowWithUniqueId = (row: ObjectDataRecord, existingUniqueId?: string) => ({
    [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: existingUniqueId ?? uuid(),
    ...row,
  })

  // Вспомогательная функция для определения позиции вставки
  const getInsertPosition = (
    data: ObjectDataRecord[],
    row: ObjectDataRecord,
    hasSorting: boolean,
    sort?: SortType[]
  ) => {
    if (!hasSorting || !sort) {
      return 0 // Без сортировки - в начало
    }
    const position = binarySearchFirstOccurrence(data, row, sort)

    return position === -1 ? 0 : position
  }

  // Вспомогательная функция для dispatch подсветки
  const highlightRow = (
    changedFields: string[],
    recordId: string,
    eventName: EVENT_NAME_OBJECT_MESSAGE,
    callback?: () => void
  ) => {
    if (!entity?.id) return

    dispatch(
      setViewRows({
        changedFields,
        recordId,
        objectCode,
        eventName,
        viewId: entity.id,
      })
    )
    callback?.()
    setTimeout(() => {
      dispatch(deleteViewRow({ objectCode, recordId, viewId: entity.id }))
    }, 3000)
  }

  const deleteRowFromTable = (rowId: ID, objectCode: string) => {
    if (!pkField || !('name' in pkField)) return

    const pkFieldName = pkField.name

    const handleDeleteRowFromArray = (array: ObjectDataRecord[], rowId: ID) => {
      if (array.length === 0) return array

      const indexDeletingRow = array.findIndex(row => {
        return String(row[pkFieldName]) === String(rowId)
      })

      if (indexDeletingRow === -1) return array

      const newArray = [...array]

      newArray.splice(indexDeletingRow, 1)

      return newArray
    }

    // Подсветка удаления
    highlightRow([], String(rowId), EVENT_NAME_OBJECT_MESSAGE.DELETE)

    // Удаляем строку из выбранных по селекту
    setSelectedRows(prevArray => {
      return handleDeleteRowFromArray(prevArray, rowId)
    })

    // Удаляем строку из данных
    setObjectData(prevArray => {
      return handleDeleteRowFromArray(prevArray, rowId)
    })

    // Уменьшаем счетчик
    setTotalElements(prevState => (prevState ?? 0) - 1)
  }

  const getQuickSearchParams = () => {
    if (!hasQuickSearch) {
      return {}
    }

    return {
      sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
        entityParamsForRequest?.sortKeysForQuickSearch as string[],
        entityParamsForRequest?.searchValue as string,
        entityParamsForRequest?.searchRule as string
      ),
      searchValue: getSearchValueForRequest(
        entityParamsForRequest?.searchValue as string,
        entityParamsForRequest?.searchRule as string
      ),
      searchRule: getSearchRuleForRequest(
        entityParamsForRequest?.searchValue as string,
        entityParamsForRequest?.searchRule as string
      ),
    }
  }

  return async (msg: IMessage) => {
    const body: MessageBody = JSON.parse(msg.body)

    const { objectCode, objectId: rowId, changedFields: _changedFields, eventName } = body
    const changedFields = _changedFields || {}

    if (!objectModel) {
      return
    }

    if (!pkField || !('name' in pkField)) {
      return
    }

    // Сохраняем имя поля PK для использования в замыканиях
    const pkFieldName = pkField.name

    if (!entity?.id) {
      return
    }

    const columns = entity.columns || []
    // Несколько колонок могут быть основаны на одном field
    // Поэтому формируем объект с каждой изменённой колонкой
    const changedColumns = Object.fromEntries(
      columns
        .filter(column => has(changedFields, column.value))
        .map(column => {
          const field = column.value
          const code = column.code

          return [
            code,
            {
              field,
              value: convertValueByType(changedFields[field], column.valueType as FIELD_VALUE_TYPE),
            },
          ]
        })
    )

    // Приводим к строкам, так как id может быть number/string
    const rowSearchIndex = objectData.findIndex(row => String(row[pkFieldName]) === String(rowId))

    const objectIsAlreadyLoaded = rowSearchIndex !== -1
    const changedColumnsMap = new Map(Object.entries(changedColumns))

    // TODO Рефакторинг, жоский
    const fieldDependsOfSorting =
      currentSort && changedColumnsMap
        ? currentSort?.some(({ field }) =>
            // sortKey ? changedFieldsMap.has(sortKey) : changedFieldsMap.has(field)
            changedColumnsMap.has(field)
          )
        : false

    const fieldsValueTypeObject: Record<string, FIELD_VALUE_TYPE> = Object.fromEntries(
      objectModel.fields.map(field => [field.name, field.valueType])
    )

    const fieldIncludeRefs = Object.values(changedColumns).some(({ field }) =>
      isObjectValueType(fieldsValueTypeObject[field])
    )

    // Если записи в таблице нет и измененные поля не зависят от сортировки и нет рефов
    if (
      !objectIsAlreadyLoaded &&
      !fieldDependsOfSorting &&
      !fieldIncludeRefs &&
      eventName === EVENT_NAME_OBJECT_MESSAGE.UPDATE
    ) {
      return
    }

    switch (eventName) {
      case EVENT_NAME_OBJECT_MESSAGE.UPDATE:
        // НЕТ СОРТИРОВКИ И НЕТ РЕФА
        if (!fieldDependsOfSorting && !fieldIncludeRefs && changedColumns) {
          const changedFieldsList = Object.keys(changedColumns)

          // TODO Необходимо дописать проверку - удалить если запись больше не подходит под фильтры и добавить если теперь подходит

          highlightRow(changedFieldsList, rowId, eventName)

          setObjectData(prev => {
            const copy = [...prev]
            const rowIndex = copy.findIndex(r => String(r[pkFieldName]) === String(rowId))
            if (rowIndex === -1) return prev

            copy[rowIndex] = {
              ...prev[rowIndex],
              ...Object.fromEntries(
                Object.entries(changedColumns).map(([columnCode, { value }]) => [columnCode, value])
              ),
            }

            return copy
          })

          return
        }

        // ЕСТЬ РЕФ ИЛИ СОРТИРОВКА - нужен fetch обогащённых данных
        if ((fieldIncludeRefs || fieldDependsOfSorting) && changedColumns) {
          fetchEnrichedRow({
            objectCode,
            ids: [rowId],
            viewId: entity.id,
            contextId,
            filter: entityParamsForRequest?.filter,
            body: searchAssistantValue,
            ...getQuickSearchParams(),
          })
            .unwrap()
            .then(([newRow]) => {
              if (!newRow) {
                // Строка больше не подходит под условия фильтра
                if (objectIsAlreadyLoaded) {
                  deleteRowFromTable(rowId, objectCode)
                }

                return
              }

              const changedFieldsList = Object.keys(changedColumns)
              const isNewRow =
                objectData.findIndex(row => String(row[pkFieldName]) === String(rowId)) === -1

              // Для существующих строк подсветка ДО обновления данных
              if (!isNewRow) {
                highlightRow(changedFieldsList, rowId, eventName)
              }

              // Обновляем/добавляем данные
              setObjectData(prev => {
                const copyObjectData = [...prev]
                const currentIndex = copyObjectData.findIndex(
                  row => String(row[pkFieldName]) === String(rowId)
                )

                // Новая строка - добавляем
                if (currentIndex === -1) {
                  const newIndex = getInsertPosition(
                    copyObjectData,
                    newRow,
                    fieldDependsOfSorting,
                    currentSort
                  )
                  copyObjectData.splice(newIndex, 0, createRowWithUniqueId(newRow))

                  return copyObjectData
                }

                const existingUniqueId =
                  prev[currentIndex][CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]

                // Есть сортировка - перемещаем строку
                if (fieldDependsOfSorting && currentSort) {
                  const newIndex = binarySearchFirstOccurrence(copyObjectData, newRow, currentSort)
                  const isValidPosition = newIndex !== -1 && newIndex <= copyObjectData.length

                  if (!isValidPosition) {
                    // Позиция не найдена - обновляем на месте
                    copyObjectData[currentIndex] = createRowWithUniqueId(newRow, existingUniqueId)

                    return copyObjectData
                  }

                  // Удаляем со старого места и вставляем в новое
                  copyObjectData.splice(currentIndex, 1)
                  const adjustedIndex = newIndex > currentIndex ? newIndex - 1 : newIndex
                  copyObjectData.splice(
                    adjustedIndex,
                    0,
                    createRowWithUniqueId(newRow, existingUniqueId)
                  )

                  return copyObjectData
                }

                // Нет сортировки - обновляем на месте
                copyObjectData[currentIndex] = createRowWithUniqueId(newRow, existingUniqueId)

                return copyObjectData
              })

              // Для новых строк увеличиваем счетчик и подсветка ПОСЛЕ добавления данных
              if (isNewRow) {
                setTotalElements(prevState => (prevState ?? 0) + 1)
                highlightRow(changedFieldsList, rowId, EVENT_NAME_OBJECT_MESSAGE.CREATE)
              }
            })

          return
        }

        return
      case EVENT_NAME_OBJECT_MESSAGE.CREATE:
        fetchEnrichedRow({
          objectCode,
          ids: [rowId],
          viewId: entity.id,
          contextId,
          filter: entityParamsForRequest?.filter,
          body: searchAssistantValue,
          ...getQuickSearchParams(),
        })
          .unwrap()
          .then(([newRow]) => {
            if (!newRow || !currentSort) return

            const position = getInsertPosition(objectData, newRow, !!currentSort, currentSort)

            setObjectData(prev => {
              const copyObjectData = [...prev]
              copyObjectData.splice(position, 0, createRowWithUniqueId(newRow))

              return copyObjectData
            })

            setTotalElements(prevState => (prevState ?? 0) + 1)
            highlightRow(Object.keys(newRow), rowId, eventName)
          })

        return
      case EVENT_NAME_OBJECT_MESSAGE.DELETE:
        if (objectIsAlreadyLoaded) {
          deleteRowFromTable(rowId, objectCode)
        }

        return
      default:
        return
    }
  }
}
